import { RobotApi, RobotTypeDefinitionItem } from 'api/RobotApi'
import { RobotColorEnum } from 'api/schema'
import clsx from 'clsx'
import { Button } from 'components/Button/Button'
import Dropdown, { IOption } from 'components/Dropdown/Dropdown'
import TextFieldDescribed from 'components/TextFieldDescribed/TextFieldDescribed'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from 'react-modal'
import { GAUSIUM_ENABLED } from 'utils/constants'
import { IRobotInfo, IStation } from 'utils/interfaces'
import type { VendorCode } from 'utils/vendorDisplayName'
import { VENDOR } from 'utils/vendorDisplayName'

import style from './AddRobotsModal.module.css'

interface IAddRobotModal {
  isOpen: boolean
  onClose: () => void
  onSave: () => void
  onStationsChanged: () => void
  className?: string
  robotExisting?: IRobotInfo
  stations: IStation[]
  robots: IRobotInfo[]
}

enum STATION_OPTIONS {
  NONE = 'none',
}

const AddRobotModal: React.FC<IAddRobotModal> = props => {
  const { t } = useTranslation()
  const [saving, setSaving] = useState(false)
  const [name, setName] = useState<string>(props.robotExisting?.name || t('robots.new_robot_name'))
  const [description, setDescription] = useState(props.robotExisting?.description || '')
  const [simNumber, setSimNumber] = useState(props.robotExisting?.sim_card_number || '')
  const getID = () => {
    return props.robotExisting ? props.robotExisting.serialNumber : ''
  }
  const [serialId, setSerialId] = useState(getID())

  const station = props.stations.find(station => station.id === props.robotExisting?.stationId)

  const [selectedStation, setSelectedStation] = useState<IStation | undefined>(station)

  const selectStationOptions: IOption[] = [
    { text: 'No maintaince station', value: STATION_OPTIONS.NONE },
    ...props.stations.map(station => {
      return { text: station.name, value: station.id }
    }),
  ]

  const robotColorOptions: IOption[] = Object.values(RobotColorEnum)
    .filter(value => typeof value === 'string')
    .map(color => ({
      text: color,
      value: color,
    }))

  const vendorOptions: IOption[] = GAUSIUM_ENABLED
    ? [
        { text: 'Autonomics', value: VENDOR.AUTONOMICS },
        { text: 'Gausium', value: VENDOR.GAUSIUM },
      ]
    : [{ text: 'Autonomics', value: VENDOR.AUTONOMICS }]

  const [robotTypeDefinitions, setRobotTypeDefinitions] = useState<RobotTypeDefinitionItem[]>([])
  const [robotTypeOptions, setRobotTypeOptions] = useState<IOption[]>([])
  const [robotType, setRobotType] = useState<string | null>(props.robotExisting?.robot_type ?? null)
  const [robotColor, setRobotColor] = useState(props.robotExisting?.robot_color || 'White')
  const [vendor, setVendor] = useState<VendorCode>((props.robotExisting?.vendor as VendorCode) || VENDOR.AUTONOMICS)

  const [currentStationOption, setCurrentStationOption] = useState(
    station ? { text: station.name, value: station.id } : selectStationOptions[0]
  )

  const [robotTypeOption, setRobotTypeOption] = useState<IOption>({ text: '—', value: '' })

  const [robotColorOption, setRobotColorOption] = useState(
    robotColor ? { text: robotColor, value: robotColor } : robotColorOptions[0]
  )
  const [vendorOption, setVendorOption] = useState(
    () => vendorOptions.find(o => o.value === vendor) ?? vendorOptions[0]
  )

  const loadTypesForVendor = useCallback(
    async (vendorCode: VendorCode, preserveSelection: boolean = false, codeToPreserve: string | null = null) => {
      const list = await RobotApi.fetchRobotTypeDefinitions(vendorCode)
      setRobotTypeDefinitions(list)
      const options: IOption[] = list.map(t => ({ text: t.display_name, value: t.code }))
      setRobotTypeOptions(options)
      if (options.length === 0) return
      if (preserveSelection && codeToPreserve) {
        const found = list.find((t: RobotTypeDefinitionItem) => t.code === codeToPreserve)
        if (found) {
          setRobotType(found.code)
          setRobotTypeOption({ text: found.display_name, value: found.code })
          return
        }
      }
      setRobotType(list[0].code)
      setRobotTypeOption(options[0])
    },
    []
  )

  useEffect(() => {
    if (!props.isOpen) return
    const preserve = !!props.robotExisting
    const codeToPreserve = preserve ? (props.robotExisting?.robot_type ?? null) : null
    const requestedVendor = GAUSIUM_ENABLED ? vendor : VENDOR.AUTONOMICS
    loadTypesForVendor(requestedVendor, preserve, codeToPreserve)
  }, [props.isOpen, loadTypesForVendor, props.robotExisting, vendor])

  useEffect(() => {
    if (props.isOpen && props.robotExisting?.robot_type && robotTypeDefinitions.length > 0) {
      const t = robotTypeDefinitions.find((d: RobotTypeDefinitionItem) => d.code === props.robotExisting!.robot_type)
      if (t) {
        setRobotType(t.code)
        setRobotTypeOption({ text: t.display_name, value: t.code })
      }
    }
  }, [props.isOpen, props.robotExisting?.robot_type, robotTypeDefinitions])

  const onSaveClick = async () => {
    try {
      setSaving(true)
      const selectedType = robotTypeDefinitions.find(t => t.code === robotType)
      const robotTypeCode = selectedType?.code ?? 'Sweeper'
      if (props.robotExisting) {
        await RobotApi.patchRobot(
          props.robotExisting.id,
          name,
          description,
          selectedStation ? selectedStation.id : undefined,
          simNumber,
          robotTypeCode,
          robotColor,
          undefined
        )
      } else {
        await RobotApi.createRobot(
          name,
          description,
          serialId,
          selectedStation ? selectedStation.id : undefined,
          simNumber,
          robotTypeCode,
          robotColor,
          GAUSIUM_ENABLED ? vendor : VENDOR.AUTONOMICS
        )
      }
      props.onSave()
    } catch (error) {
      console.log('Error creating robot')
    }
    setSaving(false)
  }

  const onCancelClick = () => {
    setSelectedStation(props.robotExisting ? station : undefined)
    setCurrentStationOption(
      props.robotExisting && station
        ? { text: station.name, value: station.id }
        : { text: 'No maintenance station', value: STATION_OPTIONS.NONE }
    )

    setRobotType(props.robotExisting?.robot_type ?? null)
    if (robotTypeOptions.length > 0) {
      const opt = robotTypeOptions.find(o => o.value === props.robotExisting?.robot_type)
      setRobotTypeOption(opt ?? robotTypeOptions[0])
    }

    setRobotColor(props.robotExisting?.robot_color || 'White')
    setRobotColorOption(
      props.robotExisting
        ? { text: props.robotExisting.robot_color, value: props.robotExisting.robot_color }
        : robotColorOptions[0]
    )

    const restoredVendor = ((props.robotExisting?.vendor as VendorCode) || VENDOR.AUTONOMICS) as VendorCode
    setVendor(restoredVendor)
    setVendorOption(vendorOptions.find(o => o.value === restoredVendor) ?? vendorOptions[0])

    setName(props.robotExisting?.name || t('robots.new_robot_name'))
    setDescription(props.robotExisting?.description || '')
    setSerialId(getID())

    props.onClose()
  }

  const onStationSelected = (e: IOption) => {
    if (e.value === STATION_OPTIONS.NONE) {
      setCurrentStationOption({ text: 'No maintenance station', value: STATION_OPTIONS.NONE })
    } else {
      setCurrentStationOption(e)
      const station = props.stations.find(station => station.id === e.value)
      setSelectedStation(station)
    }
  }

  const onTypeSelected = (e: IOption) => {
    setRobotTypeOption(e)
    setRobotType(typeof e.value === 'string' ? e.value : null)
  }

  const onColorSelected = (e: IOption) => {
    setRobotColorOption(e)
    setRobotColor(e.value as string)
  }

  const onVendorSelected = (e: IOption) => {
    setVendorOption(e)
    const newVendor = e.value as VendorCode
    setVendor(newVendor)
    loadTypesForVendor(newVendor, false, null)
  }

  const serialIdError = useMemo(() => {
    return !props.robotExisting && !!props.robots.find(robot => robot.serialNumber === serialId)
  }, [props.robotExisting, props.robots, serialId])

  const isNameValid = /^[a-zA-Z0-9\s\-_]+$/.test(name) && /[a-zA-Z0-9]/.test(name)
  const isSaveDisabled = name === '' || !isNameValid || !serialId || serialIdError

  return (
    <Modal
      isOpen={props.isOpen}
      onRequestClose={props.onClose}
      className={clsx(style.root, props.className)}
      shouldCloseOnOverlayClick={true}
      style={{ overlay: { zIndex: 100 } }}
      ariaHideApp={false}
    >
      <div className={style.titleBlock}>
        <div className={style.title}>{props.robotExisting ? t('robots.edit_existing') : t('robots.add_new')}</div>
      </div>
      <div className={style.row}>
        <TextFieldDescribed
          className={style.textfield}
          description={t('robots.robot_name')}
          defaultValue={name}
          placeholder={t('robots.robot_name_placeholder')}
          onChange={e => setName(e.target.value)}
          error={'Name must contain letters or numbers'}
          hasError={!isNameValid}
        />
        <TextFieldDescribed
          className={style.textfield}
          description={t('robots.robot_serial_id')}
          defaultValue={String(serialId)}
          placeholder={t('robots.robot_serial_id_placeholder')}
          onChange={e => setSerialId(e.target.value)}
          disabled={!!props.robotExisting}
          type="text"
          error="Serial ID must be unique"
          hasError={serialIdError}
        />
      </div>

      <div className={style.row}>
        <Dropdown
          className={style.textfield}
          currentOption={robotTypeOption}
          options={robotTypeOptions}
          onClickOption={onTypeSelected}
          description="Select robot's type:"
          disabled={!!props.robotExisting}
        />
        <Dropdown
          className={style.textfield}
          currentOption={robotColorOption}
          options={robotColorOptions}
          onClickOption={onColorSelected}
          description="Select robot's color:"
        />
        {GAUSIUM_ENABLED && (
          <Dropdown
            className={style.textfield}
            currentOption={vendorOption}
            options={vendorOptions}
            onClickOption={onVendorSelected}
            description="Select robot's vendor:"
            disabled={!!props.robotExisting}
          />
        )}
      </div>

      <div className={style.row}>
        <TextFieldDescribed
          className={style.textfield}
          description={t('robots.sim_number')}
          defaultValue={simNumber}
          placeholder={t('robots.sim_number_placeholder')}
          onChange={e => setSimNumber(e.target.value)}
        />
        <Dropdown
          className={style.textfield}
          currentOption={currentStationOption}
          options={selectStationOptions}
          onClickOption={onStationSelected}
          description="Select maintenance station:"
        />
      </div>
      <div className={style.row}>
        <TextFieldDescribed
          className={style.textfield}
          description={t('robots.robot_description')}
          defaultValue={description}
          placeholder={t('robots.robot_description_placeholder')}
          onChange={e => setDescription(e.target.value)}
        />
      </div>
      <div className={style.footer}>
        <Button.Outlined className={style.btn} disabled={saving} onClick={onCancelClick}>
          {t('common.cancel')}
        </Button.Outlined>
        <Button.Standart
          className={style.btn}
          disabled={saving || isSaveDisabled}
          loading={saving}
          onClick={onSaveClick}
        >
          {t('common.save')}
        </Button.Standart>
      </div>
    </Modal>
  )
}

export default AddRobotModal
